import React from 'react';
import {
    Box,
    Button,
    Divider,
    Grid,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { createStyleSheet } from 'utils/helpers';
import { useForm } from 'react-hook-form';
import BaseTextField from 'common/components/base-text-field';
import { common } from '@mui/material/colors';
import { yupResolver } from '@hookform/resolvers/yup';
import { registerAdminSchema } from 'common/schema';
import { useNavigate } from 'react-router-dom';
import { IRegisterSchema } from 'types/auth';
import { useTranslation } from 'react-i18next';
import FormProvider from 'common/components/form-provider';
import { signUp } from 'services/auth.service';
import { useAppDispatch } from 'common/hook';
import { setIsLoading } from 'slices/loadingSlice';
import { toast } from 'react-toastify';
import { BaseCheckbox } from 'common/components/base-checkbox-group';
import AuthNotification from '../components/auth-guide-dialog';
import background from 'assets/images/backgrounds/pre-register-bg.png';
import { ErrorCodes } from 'common/constants';
import BaseTextPasswordField from 'common/components/base-text-password-field';
import AccountNotActivatedDialog from '../components/account-not-activated-dialog';
import RequiredLabel from 'common/components/required-label';
import fbIcon from 'assets/images/icons/fb-icon-filled.png';
import ggIcon from 'assets/images/icons/gg-icon.svg';
import useLogin from 'common/hooks/use-login';
import FacebookLogin from '@greatsumini/react-facebook-login';

const Register = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispath = useAppDispatch();
    const methods = useForm({
        resolver: yupResolver(registerAdminSchema),
        mode: 'onSubmit',
        defaultValues: {
            email: '',
            password: '',
            password_confirmation: '',
            termsAndConditions: true,
            receiveEmail: true,
        },
    });
    const [valueTab, setValueTab] = React.useState('email');
    const [openDialogSuccess, setOpenDialogSuccess] =
        React.useState<boolean>(false);
    const [openActivationDialog, setOpenActivationDialog] =
        React.useState<boolean>(false);

    const { handleSubmit, setError } = methods;

    const onSubmit = handleSubmit(async (data) => {
        dispath(setIsLoading(true));
        const { email } = data as IRegisterSchema;
        const dataForm = {
            admin: {
                email: email,
                password: data.password,
                password_confirmation: data.password_confirmation,
                is_mail_subscription: data.receiveEmail,
            },
        };
        try {
            const response = await signUp(dataForm);
            if (response.success) {
                setOpenDialogSuccess(true);
                dispath(setIsLoading(false));
            } else {
                Object.entries(response.message).forEach(([key, value]) => {
                    setError(key as keyof IRegisterSchema, {
                        type: 'manual',
                        message: value,
                    });
                });
                if (response.status_code === ErrorCodes.CONFIRM_EMAIL) {
                    setOpenActivationDialog(true);
                }
                dispath(setIsLoading(false));
            }
        } catch (error) {
            if (error instanceof Error) {
                toast.error(error.message);
            }
            dispath(setIsLoading(false));
        }
    });
    const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
        setValueTab(newValue);
    };
    const { loginWithGoogle, loginWithFacebook, onSuccessLoginFacebook } =
        useLogin();
    return (
        <Box sx={styles.layout}>
            <Box
                sx={{
                    flex: 2,
                    backgroundImage: `url(${background})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'bottom',
                    backgroundRepeat: 'no-repeat',
                }}
            ></Box>
            <Box
                sx={{
                    flex: 1,
                    backgroundColor: common.white,
                }}
            >
                <Box padding={6}>
                    <Typography
                        variant="h3"
                        sx={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            lineHeight: '28px',
                            marginTop: '24px',
                            marginBottom: '12px',
                        }}
                    >
                        {t('common.welcome.site')}
                    </Typography>
                    <Tabs value={valueTab} onChange={handleChangeTab}>
                        <Tab
                            value="email"
                            label={t('common.email')}
                            sx={{ textTransform: 'none' }}
                        />
                        <Tab
                            value="phone"
                            label={t('common.phone')}
                            sx={{ textTransform: 'none' }}
                        />
                    </Tabs>

                    <FormProvider methods={methods} onSubmit={onSubmit}>
                        <Grid container>
                            {valueTab === 'email' && (
                                <>
                                    <Grid item xs={12} md={12} mt={2}>
                                        <Stack
                                            marginBottom={'17px'}
                                            spacing={'7px'}
                                        >
                                            <RequiredLabel
                                                label={t('common.email')}
                                                sx={{
                                                    fontSize: '14px',
                                                    marginLeft: 2,
                                                }}
                                                required={false}
                                            />
                                            <BaseTextField
                                                autoComplete="off"
                                                name="email"
                                                fullWidth
                                                size="small"
                                                placeholder={t(
                                                    'placeholder.enter.email'
                                                )}
                                                sx={styles.styleInput}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <Stack
                                            marginBottom={'17px'}
                                            spacing={'7px'}
                                        >
                                            <RequiredLabel
                                                label={t('common.password')}
                                                sx={{
                                                    fontSize: '14px',
                                                    marginLeft: 2,
                                                }}
                                                required={false}
                                            />
                                            <BaseTextPasswordField
                                                autoComplete="off"
                                                name="password"
                                                fullWidth
                                                size="small"
                                                placeholder={t(
                                                    'placeholder.enter.password'
                                                )}
                                                sx={styles.styleInput}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <Stack
                                            marginBottom={'17px'}
                                            spacing={'7px'}
                                        >
                                            <RequiredLabel
                                                label={t(
                                                    'common.enter.password.again'
                                                )}
                                                sx={{
                                                    fontSize: '14px',
                                                    marginLeft: 2,
                                                }}
                                                required={false}
                                            />
                                            <BaseTextPasswordField
                                                autoComplete="off"
                                                name="password_confirmation"
                                                fullWidth
                                                size="small"
                                                placeholder={t(
                                                    'placeholder.enter.password.again'
                                                )}
                                                sx={styles.styleInput}
                                            />
                                        </Stack>
                                    </Grid>
                                </>
                            )}
                            {valueTab === 'phone' && (
                                <>
                                    <Grid item xs={12} md={12} mt={2}>
                                        <Stack
                                            marginBottom={'18px'}
                                            spacing={'7px'}
                                        >
                                            <RequiredLabel
                                                label={t('common.phone')}
                                                sx={{
                                                    fontSize: '14px',
                                                    marginLeft: 2,
                                                }}
                                                required={false}
                                            />
                                            <BaseTextField
                                                autoComplete="off"
                                                name="phone"
                                                fullWidth
                                                size="small"
                                                placeholder={t(
                                                    'placeholder.enter.phone'
                                                )}
                                                sx={styles.styleInput}
                                            />
                                        </Stack>
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12} md={12} marginBottom={'24px'}>
                                <BaseCheckbox
                                    name="termsAndConditions"
                                    label={
                                        <span>
                                            <span style={styles.textLink}>
                                                {t('page.register.agree.with')}
                                            </span>
                                            <Typography
                                                component={'span'}
                                                sx={{
                                                    ...styles.textLink,
                                                    color: 'primary.main',
                                                }}
                                                onClick={() =>
                                                    navigate(
                                                        '/terms-and-conditions'
                                                    )
                                                }
                                            >
                                                &nbsp;
                                                {t('page.register.argee.terms')}
                                            </Typography>
                                        </span>
                                    }
                                />
                                <BaseCheckbox
                                    name="receiveEmail"
                                    label={
                                        <span style={styles.textLink}>
                                            {t('page.register.argee.email')}
                                        </span>
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={12} marginBottom={'24px'}>
                                <Button
                                    sx={styles.buttonRegister}
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                >
                                    {t('common.sign.up')}
                                </Button>
                            </Grid>
                            <Divider
                                sx={{
                                    width: '100%',
                                    marginBottom: '24px',
                                    color: '#8F959E',
                                }}
                            >
                                {t('register.add.login.option')}
                            </Divider>
                            <Grid item xs={12} md={12}>
                                <Stack spacing={2}>
                                    <Button
                                        onClick={() => loginWithGoogle()}
                                        variant="outlined"
                                        sx={{
                                            ...styles.button,
                                            fontSize: '14px',
                                            color: '#333333',
                                        }}
                                        color="primary"
                                        fullWidth
                                        startIcon={
                                            <img src={ggIcon} alt="Google" />
                                        }
                                    >
                                        {t('register.continue.with.google')}
                                    </Button>
                                    <FacebookLogin
                                        appId={
                                            process.env
                                                .REACT_APP_FACEBOOK_APP_ID || ''
                                        }
                                        onSuccess={onSuccessLoginFacebook}
                                        onFail={() => {
                                            toast.error(
                                                'Kết nối đến Facebook thất bại, vui lòng thử lại.'
                                            );
                                        }}
                                        scope="public_profile,email"
                                        fields="id,name,email,picture"
                                        onProfileSuccess={(response) => {
                                            loginWithFacebook(response);
                                        }}
                                        render={(renderProps) => (
                                            <Button
                                                onClick={renderProps.onClick}
                                                variant="outlined"
                                                sx={{
                                                    ...styles.button,
                                                    fontSize: '14px',
                                                    color: '#333333',
                                                }}
                                                color="primary"
                                                fullWidth
                                                startIcon={
                                                    <img
                                                        src={fbIcon}
                                                        alt="Facebook"
                                                    />
                                                }
                                            >
                                                {t(
                                                    'register.continue.with.facebook'
                                                )}
                                            </Button>
                                        )}
                                    />
                                </Stack>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                md={12}
                                textAlign={'center'}
                                mt={2}
                            >
                                <Typography
                                    sx={{
                                        ...styles.textLink,
                                    }}
                                    component={'p'}
                                    gutterBottom
                                >
                                    {t('page.register.have.account')}
                                    <Typography
                                        sx={{
                                            ...styles.textLink,
                                            color: 'primary.main',
                                        }}
                                        component={'a'}
                                        onClick={() => navigate('/login')}
                                    >
                                        &nbsp; {t('page.register.login.now')}
                                    </Typography>
                                </Typography>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </Box>
            </Box>

            {openDialogSuccess && (
                <AuthNotification
                    title="Chào mừng bạn đến với SurveyTrue!"
                    guideType="active"
                    open={openDialogSuccess}
                    onClose={() => {
                        setOpenDialogSuccess(false);
                    }}
                />
            )}
            {openActivationDialog && (
                <AccountNotActivatedDialog
                    open={openActivationDialog}
                    onClose={() => setOpenActivationDialog(false)}
                />
            )}
        </Box>
    );
};

const styles = createStyleSheet({
    layout: {
        width: '100vw',
        minHeight: '100vh',
        overflowY: 'auto',
        display: 'flex',
        backgroundAttachment: 'fixed',
    },
    registerForm: {
        width: '446px',
        padding: '25px',
        background: 'transparent',
        borderRadius: 2,
    },
    screenName: {
        color: common.white,
        fontSize: '40px',
        fontWeight: '800',
        textAlign: 'center',
        marginBottom: '28px',
    },
    textLink: {
        cursor: 'pointer',
        fontSize: '14px',
    },
    button: {
        lineHeight: '24.2px',
        textTransform: 'none',
        height: 42,
        borderRadius: '6px',
    },
    buttonRegister: {
        textTransform: 'none',
        fontSize: '16px',
    },
    styleInput: {
        '& .MuiInputBase-root': {
            height: '48px',
            fontSize: '16px',
            borderRadius: '6px',
        },
    },
});

export default Register;
